/**
 * 站内信系统 - 类型定义
 * 
 * 包含消息相关的所有类型定义
 * 
 * @author AI Assistant
 * @created 2025-12-02
 */

/**
 * 消息类型（站内信）
 */
export enum MessageType {
  /** 系统通知 */
  SYSTEM_NOTIFICATION = 'SYSTEM_NOTIFICATION',
  /** 私信 */
  PRIVATE_MESSAGE = 'PRIVATE_MESSAGE',
}

/**
 * 消息优先级
 */
export enum MessagePriority {
  /** 高优先级 */
  HIGH = 'HIGH',
  /** 普通优先级 */
  NORMAL = 'NORMAL',
  /** 低优先级 */
  LOW = 'LOW',
}

/**
 * 消息优先级中文映射
 */
export const MessagePriorityLabel: Record<MessagePriority, string> = {
  [MessagePriority.HIGH]: '高优先级',
  [MessagePriority.NORMAL]: '普通',
  [MessagePriority.LOW]: '低优先级',
}

/**
 * 消息优先级颜色映射
 */
export const MessagePriorityColor: Record<MessagePriority, string> = {
  [MessagePriority.HIGH]: 'danger',
  [MessagePriority.NORMAL]: 'primary',
  [MessagePriority.LOW]: 'info',
}

/**
 * 消息（站内信）
 */
export interface Message {
  /** 消息 ID */
  id: number
  /** 消息类型 */
  type: MessageType
  /** 发送者 ID (NULL 表示系统消息) */
  senderId: number | null
  /** 消息标题 */
  title: string
  /** 消息内容 (支持 HTML) */
  content: string
  /** 优先级 */
  priority: MessagePriority
  /** 创建时间 */
  createdAt: string
  /** 更新时间 */
  updatedAt: string
}

/**
 * 消息统计信息 (前后端统一)
 */
export interface MessageStats {
  /** 接收人数 */
  totalRecipients: number
  /** 已读人数 */
  readCount: number
  /** 未读人数 */
  unreadCount: number
}

/**
 * 消息详情 (含统计信息)
 */
export interface MessageDetail extends Message {
  /** 统计信息 */
  stats: MessageStats
}

/**
 * 创建消息请求（站内信）
 */
export interface CreateMessageRequest {
  /** 消息类型 (必填) */
  type: MessageType
  /** 消息标题 (必填) */
  title: string
  /** 消息内容 (必填, 支持 HTML) */
  content: string
  /** 优先级 (必填) */
  priority: MessagePriority
  /** 接收者 ID 列表 (必填, 至少1个用户) */
  userIds: number[]
}

/**
 * 更新消息请求（站内信）
 */
export interface UpdateMessageRequest {
  /** 消息标题 (可选) */
  title?: string
  /** 消息内容 (可选, 支持 HTML) */
  content?: string
  /** 优先级 (可选) */
  priority?: MessagePriority
}

/**
 * 消息列表查询参数（站内信）
 */
export interface MessageListParams {
  /** 页码 */
  page?: number
  /** 每页数量 */
  limit?: number
  /** 优先级筛选 */
  priority?: MessagePriority
  /** 搜索关键词 (标题/内容) */
  search?: string
}

/**
 * 分页信息
 */
export interface Pagination {
  /** 当前页码 */
  page: number
  /** 每页数量 */
  limit: number
  /** 总数 */
  total: number
  /** 总页数（可选，由 Pagination 组件内部计算） */
  totalPages?: number
}

/**
 * 消息列表响应
 */
export interface MessageListResponse {
  /** 消息列表 */
  messages: Message[]
  /** 分页信息 */
  pagination: Pagination
}

/**
 * 消息详情API响应
 */
export interface MessageDetailAPIResponse {
  /** 消息对象 */
  message: Message
  /** 统计信息 */
  stats: MessageStats
}

